import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain, FaCheck, FaTimes } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const Mcleodganj = () => {
  const tourData = {
    name: "Mcleodganj Bir Billing",
    duration: "02 Nights 03 Days",
    price: "₹ 6,499/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1755615428/wrxxewoeatdlq4glhizo.jpg",
    altitude: "6,500 ft",
    difficulty: "Easy",
    region: "Himachal Pradesh",
    highlights: ["Triund Trek", "Paragliding", "Dalai Lama Temple"],
    bestTime: "March to November",
    description: "Experience the best of Mcleodganj and Bir Billing in this 2-night, 3-day adventure. Explore the spiritual town of Mcleodganj, visit the famous Triund trek, and experience the thrill of paragliding at Bir Billing - the paragliding capital of India."
  };

  return (
    <>
      <Helmet>
        <title>{tourData.name} - {companyInfo.name}</title>
        <meta name="description" content={tourData.description} />
        <meta name="keywords" content="Mcleodganj, Bir Billing, Triund Trek, Paragliding, Himachal Pradesh, Adventure Tour" />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <section className="tour-hero">
          <div className="hero-background">
            <img src={tourData.image} alt={tourData.name} />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {tourData.description}
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div className="info-card" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>This tour offers a perfect blend of spirituality, adventure, and natural beauty, taking you through the charming town of Mcleodganj and the thrilling experience of Bir Billing paragliding.</p>
                </motion.div>

                <motion.div className="highlights-card" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
                  <h3>Highlights</h3>
                  <ul>
                    <li>Explore the spiritual town of Mcleodganj</li>
                    <li>Triund trek with breathtaking views</li>
                    <li>Paragliding experience at Bir Billing</li>
                    <li>Visit to Dalai Lama Temple</li>
                    <li>Local Tibetan culture and cuisine</li>
                    <li>Professional tour guide</li>
                    <li>24/7 customer support</li>
                  </ul>
                </motion.div>

                <motion.div className="itinerary-card" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.4 }}>
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: Depart from Delhi</h4>
                    <ul>
                      <li>Depart from Delhi at night for an overnight journey</li>
                      <li>Travel to Mcleodganj in Himachal Pradesh</li>
                      <li>Enjoy the scenic drive through the mountains</li>
                      <li>Reach Mcleodganj by early morning</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Mcleodganj Exploration</h4>
                    <ul>
                      <li>Morning: Arrive in Mcleodganj and check into accommodation</li>
                      <li>Afternoon: Explore Mcleodganj town and Dalai Lama Temple</li>
                      <li>Evening: Local market visit and Tibetan cuisine experience</li>
                      <li>Meals: Breakfast, Lunch, Dinner</li>
                      <li>Stay: Hotel/Homestay in Mcleodganj</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Triund Trek & Return</h4>
                    <ul>
                      <li>Early morning: Start Triund trek (9 km round trip)</li>
                      <li>Enjoy breathtaking views of the Dhauladhar range</li>
                      <li>Afternoon: Return to Mcleodganj and depart for Delhi</li>
                      <li>Evening: Begin return journey to Delhi</li>
                      <li>Reach Delhi by next morning</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Transportation: All transfers from Delhi to Delhi</li>
                    <li>Meals: 2 breakfasts, 2 lunches, and 2 dinners</li>
                    <li>2 Nights Stay in Hotel/Homestay at Mcleodganj</li>
                    <li>Trekking equipment and safety gear</li>
                    <li>Professional tour guide</li>
                    <li>24/7 customer support</li>
                    <li>All necessary permits and entry fees</li>
                    <li>First aid kit and emergency support</li>
                    <li>Safety equipment and communication devices</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>5% GST if applicable</li>
                    <li>Personal expenses and tips</li>
                    <li>Food on the way to and from Pick and Drop</li>
                    <li>Any additional activities not mentioned</li>
                    <li>Travel insurance</li>
                    <li>Paragliding charges (additional cost)</li>
                    <li>Any kind of personal medication</li>
                    <li>Any additional expenses due to weather conditions or unforeseen circumstances</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div className="booking-card" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 10-15 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>6,500 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Trek Distance:</strong>
                      <span>9 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Easy</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>March to November</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person (Ex. Delhi)</h4>
                  <ul>
                    <li><strong>Quad Sharing:</strong> ₹ 6,499 + 5% GST</li>
                    <li><strong>Triple Sharing:</strong> ₹ 6,999 + 5% GST</li>
                    <li><strong>Double Sharing:</strong> ₹ 7,499 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Delhi:</strong> Mayur Vihar Metro Station b/w 08:00 PM to 09:00 PM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Explore the spiritual town of Mcleodganj</li>
                    <li>Triund trek with breathtaking views</li>
                    <li>Paragliding experience at Bir Billing</li>
                    <li>Visit to Dalai Lama Temple</li>
                    <li>Local Tibetan culture and cuisine</li>
                    <li>Professional tour guide</li>
                    <li>24/7 customer support</li>
                    <li>Scenic mountain landscapes</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default Mcleodganj;
